import React, { useMemo } from 'react';
import { ElementLoader } from 'ui';
import { mapCheckedValues } from '../utils/map-checked-values';
import { UserMembersTranslation } from '../constants';
import {
  useDetailsSelectors, useListActionsSelectors,
  useListActionsCallbacks, useListBranchSelectors, useListBranchCallbacks,
  useListSelectors, useListCallbacks, useActionCreators
} from '../hooks';
import { Visibility } from '../types-store';
import { UserMembersListHeader } from './user-members-list-header';
import { UserMembersListItems } from './user-members-list-items';
import { getCurrentBranchItems } from 'app/area/subscriber-groups/user-members/utils/get-current-branch-items';
import { activeItemSelected } from 'app/area/subscriber-groups/user-members/utils/selected-value';
import { SearchFilters } from 'app/area/search-filters/search-filters';
import { predefinedFilters, userMembersFilters } from 'app/area/subscriber-groups/user-members/utils/filter-configuration';
import { UserMembersListHeading } from 'app/area/subscriber-groups/user-members/components/user-members-list-heading';
import { SerializePayload } from 'app/area/search-filters/types';
import { serialize } from 'app/area/subscriber-groups/user-members/utils/serialize';
import { deepEquals } from 'app/infrastructure/js';
import styles from './user-members-results.scss';

interface UserMembersResultsProps {
  subscriberGroupId: number;
  hasChanges: boolean;
  translation: UserMembersTranslation;
}

export const UserMembersResults: React.FC<UserMembersResultsProps> = ({
  subscriberGroupId, hasChanges, translation
}) => {

  const { listBranch, listBranchState } = useListBranchSelectors();
  const { listActions } = useListActionsSelectors();
  const { subscriberGroupValue, activeValue } = useDetailsSelectors();
  const { visiblity, selectedRows, requestedModel } = useListSelectors();
  const { loadListBranch, clearList } = useActionCreators();
  const { selectRow } = useListCallbacks();

  const {
    loadFromParent
  } = useListBranchCallbacks();

  const { addListAction } = useListActionsCallbacks();

  const updateSearchModel = (payload: SerializePayload) => {

    const serializedPayload = serialize(payload);
    const { name, email, hideDisabledUsers } = requestedModel ?? {};
    const requestedSearchModel = Object.fromEntries(
      Object.entries({ name, email, hideDisabledUsers }).filter(([_, value]) => value !== undefined)
    );

    if (!deepEquals(serializedPayload, requestedSearchModel)) {
      const model = {
        ...serializedPayload,
        subscriberGroup: subscriberGroupValue?.value,
        ...(visiblity === Visibility.all && { showAll: true }),
        ...(visiblity === Visibility.selected && { negated: false }),
        ...(visiblity === Visibility.unselected && { negated: true })
      };

      clearList(model);
      loadListBranch(model);
    }
  };

  const checkedValues = useMemo(
    () => mapCheckedValues(listBranch, listActions, activeItemSelected(activeValue)),
    [listBranch, listActions, activeValue?.value]
  );
  const id = 0;
  const currentBrunchItems = getCurrentBranchItems(id, listBranch, checkedValues, visiblity);
  const numberOfResults = listBranch?.find(branch => branch.id === 0)?.numberOfResults ?? 0;
  const fetching = listBranchState.findIndex(item => item.fetching) === -1;
  const displayNumberOfResults = visiblity === Visibility.all && listBranch?.length > 0;

  const topToolbar = <UserMembersListHeader
    subscriberGroupId={subscriberGroupId}
    hasChanges={hasChanges}
    displayNumberOfResults={displayNumberOfResults}
    numberOfResults={numberOfResults}
    translation={translation} />;

  return !!subscriberGroupValue?.value
    ? <SearchFilters
      topToolbar={undefined}
      rightToolbar={undefined}
      filtersConfig={{ area: 'subscriberGroups' }}
      queryFilters={undefined}
      hideSearch={true}
      wideScreen={true}
      displayInfoText={true}
      hideSearchLayout={true}
      predefinedFilters={predefinedFilters(translation)}
      customFilters={userMembersFilters(translation)}
      defaultFilterValues={undefined}
      updateSearchModel={updateSearchModel}>
      {topToolbar}
      {(numberOfResults > 0 && currentBrunchItems.length > 0)
        ? <div className={styles.listItemsContainer}>
          <div className={styles.listItems}>
            <UserMembersListHeading translation={translation} />
            <UserMembersListItems
              listBranch={listBranch}
              listBranchState={listBranchState}
              visiblity={visiblity}
              checkedValues={checkedValues}
              selectedRows={selectedRows}
              path={[]}
              isZebra={undefined}
              selectRow={selectRow}
              addAction={addListAction}
              loadFromParent={loadFromParent}
              translation={translation} />
          </div>
        </div> : (fetching ? <p id="user_members_number_of_results">{translation.search_no_hits}</p> : <ElementLoader />)}
    </SearchFilters>
    : <p className="no-content">{translation.choose_value_message}</p>;
};
